/**
 * Loads the Google Ads tag and sends conversion events.
 */
import { MiaPlaza } from "@/Reinforced.Typings";
import { ScriptLoader } from "@/utils/ScriptLoader";

export type GoogleAdsConversionEvent = MiaPlaza.LO.Tracking.GoogleAds.IGoogleAdsPageVisitConversionEvent
	| MiaPlaza.LO.Tracking.GoogleAds.IGoogleAdsPurchaseConversionEvent;

export type GoogleAdsTrackingOptions = Omit<MiaPlaza.Control.Tracking.IGoogleAdsTrackingOptions, "ConversionEvents"> & {
	ConversionEvents: GoogleAdsConversionEvent[]
};

export default class GoogleAds {
	private readonly options: GoogleAdsTrackingOptions;
	private loaded = false;

	constructor(options: GoogleAdsTrackingOptions) {
		this.options = options;
	}

	/**
	 * Initializes Google Ads tracking by:
	 * - Loading the gtag script
	 * - Bootstrapping the global gtag function
	 * - Configuring the conversion ID
	 * - Dispatching all pre-configured conversion events
	 *
	 * This method is idempotent and will only run once.
	 */
	public load(): void {
		if (this.loaded) {
			return;
		}
		this.loaded = true;

		ScriptLoader.LoadScript(`https://www.googletagmanager.com/gtag/js?id=${this.options.ConversionId}`);
		window.dataLayer = window.dataLayer || [];
		window.gtag = function () {
			// eslint-disable-next-line prefer-rest-params
			window.dataLayer.push(arguments);
		};

		gtag("js", new Date());
		gtag("config", this.options.ConversionId);

		for (const event of this.options.ConversionEvents) {
			this.handleConversionEvent(event);
		}
	}

	/**
	 * Handles a single Google Ads conversion event by dispatching
	 * the appropriate gtag conversion call based on its type {@link MiaPlaza.LO.Tracking.GoogleAds.GoogleAdsConversionType}
	 * @param event The conversion event to process
	 */
	private handleConversionEvent(event: GoogleAdsConversionEvent): void {
		switch (event.Type) {
			case MiaPlaza.LO.Tracking.GoogleAds.GoogleAdsConversionType.PageVisit:
				this.sendConversionEvent(event.Label);
				break;

			case MiaPlaza.LO.Tracking.GoogleAds.GoogleAdsConversionType.Purchase: {
				const purchase = event.Purchase;

				gtag("set", "user_data", {
					email: purchase.Email,
					first_name: purchase.FirstName,
					last_name: purchase.LastName,
				});

				this.sendConversionEvent(event.Label, {
					value: purchase.Value,
					currency: purchase.Currency,
					transaction_id: purchase.TransactionId,
				});
				break;
			}
		}
	}

	/**
	 * Sends a Google Ads conversion event via gtag.
	 *
	 * Automatically constructs the `send_to` parameter using the configured {@link GoogleAdsTrackingOptions.ConversionId}
	 * and the provided conversion label.
	 *
	 * @param label The Google Ads conversion label
	 * @param data Optional additional conversion parameters
	 */
	private sendConversionEvent(label: string, data: Record<string, unknown> = {}): void {
		const sendTo = `${this.options.ConversionId}/${label}`;

		gtag("event", "conversion", {
			send_to: sendTo,
			...data,
		});
	}
}
